<template>
  <v-skeleton-loader
    :loading="reservedCapacityLoading"
    :types="skeleton"
    type="article"
    class="mb-6"
    height="266"
  >
    <ValidationObserver ref="reservedCapacityForm" v-slot="{ passes }">
      <v-form rel="reservedCapacityForm" @submit.prevent="passes(save)">
        <h2 class="my-4">
          Roční rezervovaná kapacita
        </h2>

        <v-row>
          <template v-if="edit">
            <v-col cols="3">
              <v-text-field
                class="font-weight-bold"
                readonly
                outlined
                label="Stávající hodnota"
                dense
                v-model="annualCurrentBookedCapacity"
                :placeholder="annualCurrentBookedCapacity ? '' : '-'"
                suffix="kW"
              ></v-text-field>
            </v-col>

            <v-col cols="4" ref="annualBookedCapacity">
              <VTextFieldWithValidation
                :rules="{
                  required: requiredAnnualBookedCapacity,
                  numeric: true
                }"
                v-model="bookedCapacity.annualBookedCapacity"
                v-mask="'#######'"
                outlined
                label="Nová hodnota"
                dense
                suffix="kW"
                :append-outer-icon="bookedCapacity.annualBookedCapacity ? 'mdi-close' : ' '"
                @click:append-outer.prevent="clear"
              />
            </v-col>

            <v-col cols="auto" class="ml-auto mt-3 text-subtitle-1">
              Rezervovaný příkon: <b>{{ collectionPoint.reservedPowerInput }} kW</b>
            </v-col>

            <VDatePickerWithTextField
              :disabled="datePickerLoading"
              :loading="datePickerLoading"
              :rules="{
                'dateFormat': true,
                'required': false,
                'after': minForRequiredChangeDateDateFormatted,
              }"
              v-model="bookedCapacity.requiredChangeDate"
              label="Požadovaný termín změny"
              :min="minForRequiredChangeDateDateFormatted"
              :allowedDates="allowedDates"
              menuAttach=".detail-datepicker--capacity"
              cols="5"
              colClass="py-0 detail-datepicker detail-datepicker--capacity"
            />
          </template>

          <template v-else>
            <v-col cols="3" class="request-label">
              Stávající hodnota
            </v-col>

            <v-col cols="3" class="request-value">
              <div
                class="d-inline-flex align-center justify-center px-4"
              >
                {{ bookedCapacity.annualCurrentBookedCapacity || missingValueString }} kW
              </div>
            </v-col>

            <v-col cols="3" class="request-label">
              Nová hodnota
            </v-col>

            <v-col cols="3" class="request-value">
              <div
                class="d-inline-flex align-center justify-center px-4"
              >
                {{
                  edit === true ? null : (
                    bookedCapacity.annualNewBookedCapacity || missingValueString
                  )
                }}
                kW
              </div>
            </v-col>

            <v-col cols="3" class="request-label">
              Požadovaný termín změny
            </v-col>

            <v-col cols="3" class="request-value">
              <div
                class="d-inline-flex align-center justify-center px-4"
              >
                {{ dateFormatting(bookedCapacity.annualRequiredChangeDate) || missingValueString }}
              </div>
            </v-col>
          </template>
        </v-row>

        <h2 class="mt-4">
          Měsíční rezervovaná kapacita
        </h2>

        <MonthlyReservedCapacity
          v-model="bookedCapacity.monthlyBookedCapacities"
          :current-monthly-capacities="bookedCapacity.currentMonthlyCapacities"
          :edit="edit"
        />

        <v-row>
          <v-col cols="auto">
            <SquareLegend :backgroundColor="colors.actual">
              Stávající hodnota [kW]
            </SquareLegend>
          </v-col>

          <v-col cols="auto">
            <SquareLegend :bordered="edit" :backgroundColor="edit ? '' : colors.new">
              Nová hodnota [kW]
            </SquareLegend>
          </v-col>

          <v-col cols="auto">
            <SquareLegend :backgroundColor="colors.cantEdit">
              Nelze změnit
            </SquareLegend>
          </v-col>
        </v-row>

        <v-row v-if="edit">
          <v-col cols="auto" class="ml-auto">
            <v-btn
              depressed
              type="submit"
              height="40px"
              min-width="125px"
              color="primary"
              :loading="loading"
            >
              Odeslat
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>
  </v-skeleton-loader>
</template>

<script>
import { mapActions } from 'vuex';
import SquareLegend from '@/components/SquareLegend.vue';
import MonthlyReservedCapacity from '@/components/MonthlyReservedCapacity.vue';
import { onInstallationSaveSuccess } from '@/components/mixins/saveSuccess';
import VDatePickerWithTextField from '@/components/VDatePickerWithTextField.vue';

export default {
  name: 'ReservedCapacityForm',
  mixins: [onInstallationSaveSuccess],
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    individualCapacityChangeMonthly: {
      type: Object,
      default() {
        return {
          bookedCapacities: [],
        };
      },
      required: false,
    },
    collectionPoint: {
      type: Object,
      required: false,
    },
  },
  components: {
    SquareLegend,
    MonthlyReservedCapacity,
    VDatePickerWithTextField,
  },
  data() {
    return {
      skeleton: {
        article: 'heading, paragraph@2',
      },
      colors: {
        actual: '#c6d8f0',
        new: '#fff',
        cantEdit: '#d8d8d8',
      },
      bookedCapacity: {},
      annualCurrentBookedCapacity: 0,
      loading: false,
      reservedCapacityLoading: false,
      datePickerLoading: false,
      availableDates: [],
    };
  },
  methods: {
    ...mapActions({
      sendIndividualBookedCapacityRequest: 'requests/sendIndividualBookedCapacityRequest',
      getInstallationBookedCapacities: 'installations/getInstallationBookedCapacities',
    }),
    clear() {
      this.bookedCapacity.annualBookedCapacity = null;
      this.$refs.annualBookedCapacity.querySelector('input').focus();
    },
    save() {
      this.$root.$confirmationDialog
        .open('Chcete změnit rezervovanou kapacitu?', null)
        .then((resolve) => {
          if (resolve) {
            this.loading = true;
            const requestData = {
              ean: this.$route.params.ean,
              individualCapacityChangeMonthly: this.bookedCapacity,
            };
            this.sendIndividualBookedCapacityRequest(requestData).then((response) => {
              this.onInstallationSaveSuccess(response);
            }).finally(() => {
              this.loading = false;
            });
          }
        });
    },
    init() {
      if (this.individualCapacityChangeMonthly) {
        this.bookedCapacity = this.individualCapacityChangeMonthly;
      }
      if (this.edit === true) {
        this.reservedCapacityLoading = true;
        this.getInstallationBookedCapacities(this.$route.params.ean).then((response) => {
          if (response.status === 200) {
            this.bookedCapacity = response.data;
            this.annualCurrentBookedCapacity = response.data.annualBookedCapacity;
          }
        }).finally(() => {
          this.reservedCapacityLoading = false;
        });
      }
    },
    allowedDates(date) {
      return new Date(date) >= new Date() && date.endsWith('01');
    },
  },
  watch: {
    collectionPoint: {
      handler() {
        this.init();
      },
    },
  },
  computed: {
    requiredAnnualBookedCapacity() {
      /*
      if (this.bookedCapacity.monthlyBookedCapacities) {
        return Object.values(this.bookedCapacity.monthlyBookedCapacities)
          .some((monthlyBookedCapacity) => (Object.values(monthlyBookedCapacity.values)
            .some((bookedCapacity) => (bookedCapacity.value === null || bookedCapacity.value === '')
                && bookedCapacity.wasEditable === true)));

      } */
      return false;
    },
    minForRequiredChangeDateDateFormatted() {
      return this.availableDates[1];
    },
  },
  created() {
    this.init();
  },
};
</script>
